import { useState, useMemo, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  BookOpen, Plus, ChevronDown, ChevronRight, Menu,
  CalendarDays, List, Clock, FileText
} from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

export default function JournalView() {
  const {
    journalNotes, setSelectedNoteId, setSidebarOpen,
    createJournalEntry, templates, selectedNoteId,
  } = useApp();

  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' | 'calendar'
  const [collapsedDates, setCollapsedDates] = useState({});
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState(null);

  // Group entries by date
  const groupedEntries = useMemo(() => {
    const groups = {};
    const filteredNotes = selectedCalDate
      ? journalNotes.filter(n => n.entry_date === selectedCalDate)
      : journalNotes;

    filteredNotes.forEach(note => {
      const date = note.entry_date || note.created_at?.slice(0, 10) || 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(note);
    });
    return groups;
  }, [journalNotes, selectedCalDate]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedEntries).sort((a, b) => b.localeCompare(a));
  }, [groupedEntries]);

  // Dates that have entries (for calendar highlighting)
  const datesWithEntries = useMemo(() => {
    const set = new Set();
    journalNotes.forEach(n => {
      if (n.entry_date) set.add(n.entry_date);
    });
    return set;
  }, [journalNotes]);

  const toggleCollapse = useCallback((date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  }, []);

  const formatDateLabel = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      if (isYesterday(date)) return 'Yesterday';
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const truncateContent = (content, maxLen = 120) => {
    if (!content) return '';
    const plain = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
      .replace(/[#*`\[\]_~>|\\-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.length > maxLen ? plain.slice(0, maxLen) + '...' : plain;
  };

  const handleNewEntry = async (templateId = null) => {
    setShowTemplateSelect(false);
    await createJournalEntry(templateId);
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (selectedCalDate === dateStr) {
      setSelectedCalDate(null); // toggle off
    } else {
      setSelectedCalDate(dateStr);
    }
  };

  const todayHasEntry = datesWithEntries.has(format(new Date(), 'yyyy-MM-dd'));

  return (
    <div className="flex flex-col h-full w-full" data-testid="journal-view">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-border/50">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-sm text-muted-foreground hover:text-foreground active:bg-muted/50 transition-colors -ml-1"
            data-testid="journal-toggle-sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
          <BookOpen className="w-5 h-5 text-accent-amber" />
          <h2 className="flex-1 text-base font-semibold tracking-tight" data-testid="journal-title">
            Journal
          </h2>
          <span className="text-xs text-muted-foreground mr-1">{journalNotes.length} entries</span>

          {/* View toggle */}
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => { setViewMode('timeline'); setSelectedCalDate(null); }}
              className={`p-1.5 transition-colors ${viewMode === 'timeline' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              title="Timeline view"
              data-testid="journal-timeline-toggle"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-1.5 transition-colors ${viewMode === 'calendar' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              title="Calendar view"
              data-testid="journal-calendar-toggle"
            >
              <CalendarDays className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* New entry button */}
        <div className="flex gap-2">
          <Button
            onClick={() => handleNewEntry()}
            className="flex-1 bg-accent-amber text-black hover:bg-accent-amber/90 font-medium text-sm h-10"
            data-testid="journal-new-entry-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Journal Entry
          </Button>
          {templates.length > 0 && (
            <Popover open={showTemplateSelect} onOpenChange={setShowTemplateSelect}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 px-3"
                  title="Choose template"
                  data-testid="journal-template-select-btn"
                >
                  <FileText className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-1" align="end">
                <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Use template</p>
                <button
                  onClick={() => handleNewEntry()}
                  className="w-full text-left px-2 py-2 text-sm hover:bg-muted/50 rounded-sm transition-colors"
                  data-testid="journal-template-none"
                >
                  No template (blank)
                </button>
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleNewEntry(t.id)}
                    className="w-full text-left px-2 py-2 text-sm hover:bg-muted/50 rounded-sm transition-colors truncate"
                    data-testid={`journal-template-${t.id}`}
                  >
                    {t.name}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Calendar filter indicator */}
        {selectedCalDate && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-accent-amber">
              Showing entries for {formatDateLabel(selectedCalDate)}
            </span>
            <button
              onClick={() => setSelectedCalDate(null)}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="journal-clear-date-filter"
            >
              Show all
            </button>
          </div>
        )}
      </div>

      {/* Content Area */}
      <ScrollArea className="flex-1 w-full">
        {viewMode === 'calendar' && (
          <div className="p-3 border-b border-border/30">
            <Calendar
              mode="single"
              selected={selectedCalDate ? parseISO(selectedCalDate) : undefined}
              onSelect={handleCalendarSelect}
              month={calendarDate}
              onMonthChange={setCalendarDate}
              modifiers={{
                hasEntry: (date) => datesWithEntries.has(format(date, 'yyyy-MM-dd')),
              }}
              modifiersClassNames={{
                hasEntry: 'journal-has-entry',
              }}
              className="mx-auto"
              data-testid="journal-calendar"
            />
          </div>
        )}

        <div className="w-full">
          {sortedDates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="journal-empty-state">
              <BookOpen className="w-14 h-14 text-muted-foreground/15 mb-4" />
              <p className="text-base font-medium text-muted-foreground">
                {selectedCalDate ? 'No entries for this date' : 'Start your first entry today'}
              </p>
              <p className="text-sm text-muted-foreground/60 mt-1.5 max-w-[280px]">
                {selectedCalDate
                  ? 'Try selecting a different date or create a new entry.'
                  : 'Capture your thoughts, ideas, and reflections. Each entry is grouped by day.'}
              </p>
              {!selectedCalDate && (
                <Button
                  onClick={() => handleNewEntry()}
                  className="mt-5 bg-accent-amber text-black hover:bg-accent-amber/90"
                  data-testid="journal-empty-new-entry-btn"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  New Journal Entry
                </Button>
              )}
            </div>
          ) : (
            sortedDates.map(date => {
              const entries = groupedEntries[date];
              const isCollapsed = collapsedDates[date];

              return (
                <div key={date} className="border-b border-border/20" data-testid={`journal-date-group-${date}`}>
                  {/* Date Header */}
                  <button
                    onClick={() => toggleCollapse(date)}
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors sticky top-0 bg-background/95 backdrop-blur-sm z-10"
                    data-testid={`journal-date-toggle-${date}`}
                  >
                    {isCollapsed
                      ? <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    }
                    <span className="text-sm font-semibold text-foreground">
                      {formatDateLabel(date)}
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">
                      {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                    </span>
                  </button>

                  {/* Entries */}
                  {!isCollapsed && (
                    <div className="pb-2">
                      {entries.map(note => (
                        <button
                          key={note.id}
                          onClick={() => setSelectedNoteId(note.id)}
                          className={`w-full text-left px-4 py-3 ml-2 mr-2 rounded-md transition-colors ${
                            selectedNoteId === note.id
                              ? 'bg-muted/80'
                              : 'hover:bg-muted/30'
                          }`}
                          data-testid={`journal-entry-${note.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />
                            <span className="text-xs text-muted-foreground font-medium">
                              {(() => {
                                try {
                                  return format(new Date(note.created_at), 'h:mm a');
                                } catch { return ''; }
                              })()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed pl-5">
                            {(note._decryptFailed || note._needsDecryption)
                              ? '[Encrypted - unlock to view]'
                              : truncateContent(note.content) || 'Empty entry'}
                          </p>
                          {/* Tags */}
                          {note.tags && note.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
                              {note.tags.slice(0, 3).map(tagId => (
                                <span
                                  key={tagId}
                                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground"
                                >
                                  {tagId}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
